/**
 * Per-measure payload POSTed to /api/practices/:id/mips/scores/calculate — the
 * points the backend stores. Moved verbatim (P1, 2026-10-19) from
 * src/services/mips-score-calculator.ts; pinned by __tests__/category-payload.parity.test.ts.
 *
 * Benchmark resolution matches Gap Management rows (exact collection type only — backlog B18).
 */
import { scoreQualityMeasure, type PracticeSize } from '@/lib/mips-scoring';
import { findExactBenchmark, type MeasureDataSource } from './measure-math';

export interface CategoryPayloadDataSource extends MeasureDataSource {
  getMeasureMeta(measureId: string): any;
  getActiveMeasure(measureId: string): { isHighPriority?: boolean } | undefined;
}

/** The row fields the payload reads (a Gap Management row from buildGapRow satisfies it). */
export interface PayloadRow {
  id?: string;
  measureId?: string;
  title?: string;
  submissionMethod?: string;
  measureType?: string;
  type?: string;
  isHighPriority?: boolean;
  isMultiStrata?: boolean;
  den?: number;
  denominator?: number;
  met?: number;
  performanceMet?: number;
  not?: number;
  performanceNotMet?: number;
  exc?: number;
  numeratorExceptions?: number;
  pr?: number;
  overallPr?: number;
  dc?: number;
  overallDc?: number;
}

export function toCategoryPayload(measure: PayloadRow, practiceSize: PracticeSize, dataService: CategoryPayloadDataSource) {
  const measureId = (measure.id || measure.measureId) as string;
  const submissionMethod = measure.submissionMethod || 'MIPS CQM';

  // CMS benchmarks are per collection type (guide §2.3); with none for this one the measure is
  // "not reliably scored" — never scored against another collection type's benchmark (backlog B18).
  const benchmark = findExactBenchmark(dataService, measureId, submissionMethod);

  // Split-algorithm strata measures (e.g. 226, 431, 510): the row's counts are the DC
  // stratum's, so pass the row's precomputed PR/DC instead of re-deriving them.
  const strataConfig = dataService.getStrataConfig(measureId, submissionMethod);
  const isSplitStrata = measure.isMultiStrata && strataConfig &&
    strataConfig.completeRate !== strataConfig.performanceRate;
  const splitOverrides = isSplitStrata
    ? {
        overridePerformanceRate: measure.pr ?? measure.overallPr,
        overrideDataCompletenessRate: measure.dc ?? measure.overallDc,
      }
    : {};

  const scoringResult = scoreQualityMeasure({
    denominator: measure.den || measure.denominator || 0,
    performanceMet: measure.met || measure.performanceMet || 0,
    performanceNotMet: measure.not || measure.performanceNotMet || 0,
    numeratorExceptions: measure.exc || measure.numeratorExceptions || 0,
    practiceSize,
    benchmark: benchmark || null,
    ...splitOverrides,
  });

  const meta = dataService.getMeasureMeta(measureId);

  return {
    measure_id: measureId,
    title: measure.title,
    measure_type: measure.measureType || measure.type,
    // The Traditional top-6 strategy needs this flag (backlog B19); the CMS measure record is its source.
    is_high_priority: measure.isHighPriority || meta?.isHighPriority || dataService.getActiveMeasure(measureId)?.isHighPriority || false,
    is_outcome: meta?.isOutcome || ['outcome', 'intermediateOutcome', 'patientReportedOutcome'].includes(measure.measureType || ''),
    denominator: measure.den || measure.denominator || 0,
    performance_met: measure.met || measure.performanceMet || 0,
    performance_not_met: measure.not || measure.performanceNotMet || 0,
    numerator_exceptions: measure.exc || measure.numeratorExceptions || 0,
    submission_method: submissionMethod,
    calculated_points: scoringResult.score,
    performance_rate: scoringResult.performanceRate,
    data_completeness_rate: scoringResult.dataCompletenessRate,
    passes_completeness: scoringResult.passesCompleteness,
    meets_case_minimum: scoringResult.meetsCaseMinimum,
    decile: scoringResult.decile,
    applied_rule: scoringResult.appliedRule,
  };
}

/** Rows that count toward the stored score (Summary's measuresWithData filter). */
export function rowHasData(row: { den?: number; met?: number; not?: number; exc?: number }): boolean {
  return (row.den ?? 0) > 0 || (row.met ?? 0) > 0 || (row.not ?? 0) > 0 || (row.exc ?? 0) > 0;
}
